import { Component, effect, inject, OnDestroy } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormTextareaComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { ReflectionFacade } from '@app/store/reflection';

@Component({
    selector: 'app-reflection-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, FormTextareaComponent, UnavailableComponent],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly reflectionFacade = inject(ReflectionFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    private readonly id = this.route.parent?.snapshot.params['id'];
    readonly reflection = this.reflectionFacade.reflection(this.id);
    readonly loading = this.reflectionFacade.loading(this.id);
    readonly saving = this.reflectionFacade.saving;
    readonly error = this.reflectionFacade.error(this.id);

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        questionText: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
    });

    constructor() {
        if (this.id) {
            this.reflectionFacade.getReflection(this.id);
        }

        effect(() => {
            const reflection = this.reflection();
            if (!reflection) return;

            this.form.patchValue(reflection);
        });

        effect(() => {
            if (this.reflectionFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.reflectionFacade.saving() === false && this.reflectionFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.reflectionFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;

        this.form.disable();
        this.reflectionFacade.updateReflection(this.id, this.form.getRawValue());
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
